import React from 'react';
import {
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Box,
  Tooltip,
  Chip,
} from '@mui/material';
import StatusChip from './StatusChip';
import {
  Settings as SettingsIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  AutoAwesome as SparklesIcon,
} from '@mui/icons-material';
import {
  TEMPLATE_CATEGORIES,
  OUTPUT_FORMATS,
  TEMPLATE_STATUSES,
} from '../../../config/constants';

/**
 * TemplateForm Component
 *
 * Main form component for template details including basic information,
 * template content, tags, and output format selection.
 *
 * @param {Object} props - Component props
 * @param {Object} props.formData - Form data object
 * @param {boolean} props.isEditMode - Whether the editor is in edit mode
 * @param {string} props.versionId - Version ID if editing a specific version
 * @param {string} props.newTag - Current new tag input value
 * @param {Function} props.handleInputChange - Handler for form input changes
 * @param {Function} props.handleAddTag - Handler for adding tags
 * @param {Function} props.handleRemoveTag - Handler for removing tags
 * @param {Function} props.handleTagKeyPress - Handler for tag input key press
 * @param {Function} props.setNewTag - Setter for new tag input
 * @param {Function} props.onOpenAIGenerator - Handler for opening AI Template Generator dialog
 */
const TemplateForm = ({
  formData,
  isEditMode,
  versionId,
  newTag,
  handleInputChange,
  handleAddTag,
  handleRemoveTag,
  handleTagKeyPress,
  setNewTag,
  onOpenAIGenerator,
}) => {
  // When editing a specific version, template name should be read-only
  const isVersionEdit = Boolean(versionId);
  return (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        Template Details
      </Typography>

      {/* Basic Information */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Template Name"
            value={formData.name}
            onChange={e => handleInputChange('name', e.target.value)}
            placeholder="Enter template name"
            required
            InputProps={{
              readOnly: isVersionEdit,
            }}
            helperText={
              isVersionEdit
                ? 'Template name cannot be changed when editing a version'
                : undefined
            }
            sx={{ mb: 2 }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={formData.category}
              onChange={e => handleInputChange('category', e.target.value)}
              label="Category"
            >
              {TEMPLATE_CATEGORIES.map(category => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={e => handleInputChange('status', e.target.value)}
              label="Status"
              renderValue={value => {
                const s = TEMPLATE_STATUSES.find(st => st.value === value);
                if (!s) return value;
                return (
                  <StatusChip
                    label={s.label}
                    color={s.color}
                    status={s.value}
                  />
                );
              }}
            >
              {TEMPLATE_STATUSES.map(status => {
                return (
                  <MenuItem key={status.value} value={status.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StatusChip
                        label={status.label}
                        color={status.color}
                        status={status.value}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {status.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Description */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <SettingsIcon color="primary" />
          Description
        </Typography>
        <TextField
          fullWidth
          value={formData.description}
          onChange={e => handleInputChange('description', e.target.value)}
          placeholder={
            isEditMode
              ? 'Describe what changed in this version...'
              : 'Describe what this template does...'
          }
          multiline
          rows={3}
          helperText={
            isEditMode
              ? 'Explain what changes you made in this version of the template'
              : 'Provide a clear description of what this template accomplishes'
          }
        />
      </Box>

      {/* Template Content */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 2,
            mb: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <SettingsIcon color="primary" />
            Template Content
            <Chip label="Required" color="primary" size="small" />
          </Typography>
          {onOpenAIGenerator && (
            <Tooltip title="Generate template with AI" arrow>
              <Button
                variant="text"
                size="medium"
                startIcon={
                  <SparklesIcon
                    sx={theme => ({
                      fontSize: '1.1rem',
                      color: theme.palette.warning.main, // distinct golden accent
                      '@keyframes sparkleTwinkle': {
                        '0%': {
                          transform: 'scale(1) rotate(0deg)',
                          opacity: 0.8,
                        },
                        '50%': {
                          transform: 'scale(1.15) rotate(-5deg)',
                          opacity: 1,
                        },
                        '100%': {
                          transform: 'scale(1) rotate(0deg)',
                          opacity: 0.9,
                        },
                      },
                      animation: 'sparkleTwinkle 1.4s ease-in-out infinite',
                    })}
                  />
                }
                onClick={onOpenAIGenerator}
                sx={theme => ({
                  textTransform: 'none',
                  fontWeight: 700,
                  color: theme.palette.primary.dark,
                  px: 1.5,
                  minWidth: 0,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    textDecoration: 'underline',
                  },
                  '& .MuiButton-startIcon': {
                    marginLeft: theme.spacing(0.5),
                    marginRight: theme.spacing(1),
                  },
                })}
              >
                AI Template Generator
              </Button>
            </Tooltip>
          )}
        </Box>
        <TextField
          fullWidth
          value={formData.template}
          onChange={e => handleInputChange('template', e.target.value)}
          placeholder="Enter your prompt template here. Use {{variable}} for dynamic content..."
          multiline
          rows={10}
          required
          sx={{
            '& .MuiOutlinedInput-root': {
              fontFamily: 'monospace',
              fontSize: '0.9rem',
            },
          }}
          helperText={
            <Box component="div">
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                Use <code>{'{{variable}}'}</code> syntax for dynamic content.
                Example:{' '}
                <code>
                  Analyze the following {'{{topic}}'}: {'{{content}}'}
                </code>
              </Box>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  mt: 1,
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                }}
              >
                💡 Tip: Variables will be replaced with actual values when the
                template is executed.
              </Box>
            </Box>
          }
        />
      </Box>

      {/* Tags */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Tags
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            value={newTag}
            onChange={e => setNewTag(e.target.value)}
            onKeyPress={handleTagKeyPress}
            placeholder="Add a tag"
            sx={{ mr: 1, minWidth: 150 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={handleAddTag}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {formData.tags.map((tag, index) => (
            <Chip
              key={`tag-${index}-${tag}`}
              label={tag}
              onDelete={() => handleRemoveTag(tag)}
              deleteIcon={<DeleteIcon />}
              color="primary"
              variant="outlined"
            />
          ))}
        </Box>
      </Box>

      {/* Output Format */}
      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Output Format</InputLabel>
        <Select
          value={formData.output_format}
          onChange={e => handleInputChange('output_format', e.target.value)}
          label="Output Format"
        >
          {OUTPUT_FORMATS.map(format => (
            <MenuItem key={format.value} value={format.value}>
              <Box>
                <Typography variant="body1">{format.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {format.description}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>
  );
};

export default TemplateForm;
